import { useEffect, useState, useContext } from "react";
import { VisibilityContext } from "react-horizontal-scrolling-menu";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ReactComponent as LocationIcon } from "../assets/share.svg";
// Import icons for specific metadata pieces
import { ReactComponent as EligibilityIcon } from "../assets/material-symbols_person-check.svg";
import { ReactComponent as ApplicationProcessIcon } from "../assets/mdi_application-edit.svg";
import { ReactComponent as DisabilitiesAccessIcon } from "../assets/fa-solid_wheelchair.svg";
import { ReactComponent as LanguagesOfferedIcon } from "../assets/ix_language-filled.svg";
import { ServiceObject } from "../context/RecoEngineContext";
import { formatKeyLabel, createLinkParser } from "../services";
import Button from "./Button";
import Address from "./Address";
import ContactButtonRow from "./ContactButtonRow";
import OpenHours from "./OpenHours";
import { handleRedirect } from "../../../utils/CommonFunctions";

interface ServiceCardProps {
  service?: ServiceObject;
  colour?: string;
  setActiveItemID?: (itemId: string) => void;
  itemId?: string;
}

const ServiceCard = ({ service, setActiveItemID, itemId, colour }: ServiceCardProps) => {
  const history = useNavigate();

  const visibility = useContext(VisibilityContext);
  const [t] = useTranslation("translations");
  const parseWithLinks = createLinkParser(handleRedirect, history, "RECO_ENGINE");

  const [showMore, setShowMore] = useState(false);
  const [showMoreDesc, setShowMoreDesc] = useState(false);
  const [truncatedText, setTruncatedText] = useState("");

    const handleButtonClick = (button_action, title) => {
      handleRedirect(button_action, history, title, service.name);
    };

  useEffect(() => {
    if (itemId && visibility.isItemVisible(itemId)) {
      setActiveItemID?.(itemId);
    }
  }, [itemId, setActiveItemID, visibility]);

  useEffect(() => {
    if (service.description) {
      // Strip HTML and truncate to approximately 3 lines
      const plainText = service.description
      .replace(/<br\s*\/?>/gi, " ") // Replace <br> tags with spaces
      .replace(/<\/p>/gi, " ") // Replace closing </p> tags with spaces
      .replace(/<\/div>/gi, " ") // Replace closing </div> tags with spaces
      .replace(/<[^>]*>/g, "") // Remove remaining HTML tags
      .replace(/\s+/g, " ") // Replace multiple spaces with single space
      .trim(); // Remove leadin
      
      // Estimate characters per line (adjust based on your container width)
      const charsPerLine = 37; // Adjust this based on your actual container width
      const maxLines = 3;
      const maxChars = charsPerLine * maxLines;
      
      const truncated = plainText.length > maxChars 
        ? `${plainText.substring(0, maxChars).trim()  }...`
        : plainText;
      setTruncatedText(truncated);
    }
  }, [service.description]);

  const renderRegularBody = () => (
    <div className="body">
      <div className="service__header">
        <h3>{service.name}</h3>
        <button className="share-button" onClick={()=> {handleButtonClick(
          {
            type: "SHARE",
            title: service.name,
            // eslint-disable-next-line @typescript-eslint/dot-notation
            url: service.metadata?.["website"],
          },
          `share - ${service.name}`
        );}}>
          <LocationIcon />
        </button>
      </div>

      <button 
        type="button"
        className={`recoEngine__serviceCard__desc ${showMoreDesc ? "expanded" : "clamped"}`}
        onClick={() => setShowMoreDesc(!showMoreDesc)}
      >
        {showMoreDesc ? parseWithLinks(service.description) : truncatedText}
      </button>

      <Address name={service.name}  address={service.address} />
      <OpenHours metadata={service.metadata} />
      <ContactButtonRow 
        name={service.name} 
        metadata={service.metadata} 
        phone={service.phone_numbers} 
      />
    </div>
  );

  const renderButtons = () => (
    <div className="service-card-buttons">
      {showMore && <Button
        className="report-button"
        text={t("RecoSearch.report_problem")}
        onClick={() => {
          handleRedirect({
            type: "URL",
            url: `mailto:holly.hilts@identos.ca?subject=Error%20with%20${service.name}&body=service%20id: ${service.id}`,
          }, history, `Report a Problem - ${service.name}`, "RECO_ENGINE");
        }}
      />}
      <Button
        text={showMore ? t("RecoSearch.show_less") : t("RecoSearch.show_more")}
        onClick={() => setShowMore(!showMore)}
      />
    </div>
  );

  const renderEligibility = () => {
    if (!(service.metadata as any)?.eligibility) return null;
    return (
      <div className="metadata-item">
        <EligibilityIcon className="metadata-icon" />
        <div className="metadata-content">
          <strong>{formatKeyLabel("eligibility")}: </strong>
          {typeof (service.metadata as any).eligibility === "string" 
            ? parseWithLinks((service.metadata as any).eligibility) 
            : (service.metadata as any).eligibility}
        </div>
      </div>
    );
  };

  const renderApplicationProcess = () => {
    if (!(service.metadata as any)?.application_process) return null;
    return (
      <div className="metadata-item">
        <ApplicationProcessIcon className="metadata-icon" />
        <div className="metadata-content">
          <strong>{formatKeyLabel("application_process")}: </strong>
          {typeof (service.metadata as any).application_process === "string" 
            ? parseWithLinks((service.metadata as any).application_process) 
            : (service.metadata as any).application_process}
        </div>
      </div>
    );
  };

  const renderAccessibility = () => {
    if (!(service.metadata as any)?.wheelchair_accessible) return null;
    return (
      <div className="metadata-item">
        <DisabilitiesAccessIcon className="metadata-icon" />
        <div className="metadata-content">
          <strong>{formatKeyLabel("disabilities_access")}: </strong>
          {typeof (service.metadata as any).wheelchair_accessible === "string" 
            ? "Wheelchair Accessible" 
            : (service.metadata as any).disabilities_access}
        </div>
      </div>
    );
  };

  const renderLanguages = () => {
    if (!(service.metadata as any)?.languages) return null;
    return (
      <div className="metadata-item">
        <LanguagesOfferedIcon className="metadata-icon" />
        <div className="metadata-content">
          <strong>{formatKeyLabel("languages_offered")}: </strong>
          {typeof (service.metadata as any).languages === "string" 
            ? parseWithLinks((service.metadata as any).languages) 
            : (service.metadata as any).languages}
        </div>
      </div>
    );
  };

  const renderMetadataBody = () => (
    <div className="body metadata">
      {renderEligibility()}
      {renderApplicationProcess()}
      {renderAccessibility()}
      {renderLanguages()}
    </div>
  );

  return (
    <div className="recoEngine__serviceCard">
      <div className="colorBar" style={{ backgroundColor: colour }} />
      {showMore ? renderMetadataBody() : renderRegularBody()}
      {renderButtons()}
    </div>
  );
};

export default ServiceCard;
